import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { useCustomerAuth } from '../context/CustomerAuthContext';
// NOTE: use the plain async function, not the `useFileUpload` hook — the hook triggers an
// "Invalid hook call" (webpack bundles two separate React module instances across the
// frontend <-> shared symlink boundary, even though both resolve to the same react version
// on disk). uploadBlobThroughStorage does the identical presign->PUT->confirm flow with no
// React internals involved, so it's unaffected by that resolution issue.
import { uploadBlobThroughStorage } from 'shared-ui';

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
const API_BASE = `${BACKEND_URL}/api/v1`;
const CHAT_BASE = `${API_BASE}/event-chat`;
const STRIPE_PUBLISHABLE_KEY_FALLBACK = (process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '').trim();

async function fetchStripePublishableKey() {
  try {
    const businessId = process.env.REACT_APP_BUSINESS_ID || '';
    const res = await fetch(`${API_BASE}/business/stripe-public${businessId ? `?businessId=${encodeURIComponent(businessId)}` : ''}`);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    return (data.publishableKey || '').trim();
  } catch (err) {
    console.warn('Could not fetch Stripe key, using env fallback:', err.message);
    return STRIPE_PUBLISHABLE_KEY_FALLBACK;
  }
}

function CardSetupForm({ onSaved, saving, setSaving, setError }) {
  const stripe = useStripe();
  const elements = useElements();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setSaving(true);
    setError(null);
    try {
      const { error, setupIntent } = await stripe.confirmSetup({
        elements,
        redirect: 'if_required',
      });
      if (error) {
        setError(error.message || 'Could not save your card');
        setSaving(false);
        return;
      }
      onSaved(setupIntent.payment_method);
    } catch (err) {
      setError(err.message || 'Could not save your card');
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <PaymentElement />
      <button type="submit" disabled={!stripe || saving} style={{ marginTop: 16, padding: '12px 24px', fontSize: 16, cursor: 'pointer' }}>
        {saving ? 'Saving...' : "Save card — you won't be charged yet"}
      </button>
    </form>
  );
}

export default function EventSignup() {
  const { eventSlug } = useParams();
  const [searchParams] = useSearchParams();
  const referralCode = searchParams.get('ref') || '';
  const codeFromUrl = searchParams.get('code') || '';
  const partyIdFromUrl = searchParams.get('party') || '';
  const { customer, isAuthenticated, getAuthHeaders } = useCustomerAuth();

  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isPrivateLocked, setIsPrivateLocked] = useState(false);
  const [manualCode, setManualCode] = useState('');
  const [stripePromise, setStripePromise] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);
  const [stripeCustomerId, setStripeCustomerId] = useState(null);
  const [savingCard, setSavingCard] = useState(false);
  const [signupResult, setSignupResult] = useState(null); // { data, waitlisted }
  const [myReferralLink, setMyReferralLink] = useState(null);
  const [addOns, setAddOns] = useState([]);
  const [contributionForm, setContributionForm] = useState({ description: '', estimatedCost: '' });
  const [contributionSubmitted, setContributionSubmitted] = useState(false);
  const [contributionsPublic, setContributionsPublic] = useState({ data: [], runningTotal: 0, budgetCap: null });
  const [chatTopics, setChatTopics] = useState([]);
  const [selectedTopicId, setSelectedTopicId] = useState(null); // null = "All" (unified stream)
  const [chatMessages, setChatMessages] = useState([]);
  const [chatLoadError, setChatLoadError] = useState(null);
  const [chatInput, setChatInput] = useState('');
  const [chatPhotoFile, setChatPhotoFile] = useState(null);
  const [chatItemName, setChatItemName] = useState('');
  const [showItemOffer, setShowItemOffer] = useState(false);
  const [sendingChat, setSendingChat] = useState(false);
  const [roles, setRoles] = useState([]);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [newNeedName, setNewNeedName] = useState('');
  const [newNeedCategory, setNewNeedCategory] = useState('gear');
  const [addingNeed, setAddingNeed] = useState(false);
  const [offeringItemId, setOfferingItemId] = useState(null);
  const [offerPhotoFile, setOfferPhotoFile] = useState(null);
  const [offeringBusy, setOfferingBusy] = useState(false);
  const [contributionPhotoFile, setContributionPhotoFile] = useState(null);
  const [dietaryPreference, setDietaryPreference] = useState('');
  const [venueTiers, setVenueTiers] = useState([]);
  const [carCount, setCarCount] = useState(1);
  const [partySize, setPartySize] = useState(1);
  const [emergencyContactName, setEmergencyContactName] = useState('');
  const [emergencyContactPhone, setEmergencyContactPhone] = useState('');
  const [proposedAddOnForm, setProposedAddOnForm] = useState({ name: '', unitCost: '', unitYield: '', minSharePerPerson: '', unitYieldLabel: '' });
  const [proposingAddOn, setProposingAddOn] = useState(false);
  const [retryClientSecret, setRetryClientSecret] = useState(null);
  const [retryStripeCustomerId, setRetryStripeCustomerId] = useState(null);
  const [retrySaving, setRetrySaving] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [myParty, setMyParty] = useState(null);
  const [partyNameInput, setPartyNameInput] = useState('');
  const [joinPartyId, setJoinPartyId] = useState('');
  const [partyBusy, setPartyBusy] = useState(false);

  const uploadFile = (file, meta) => uploadBlobThroughStorage({ apiBaseUrl: API_BASE, tokenKey: 'customer_token', file, meta });

  const isDeferred = event?.paymentCaptureMode === 'deferred_threshold';
  const isCarTiers = event?.venueSelectionMode === 'car_tiers';
  const hasCarPersonPricing = event?.pricePerCar != null || event?.pricePerPerson != null;
  const collectsCarPerson = isCarTiers || hasCarPersonPricing;
  const overThreshold = event?.thresholdReachedAt != null;
  const currentPerPersonRate = overThreshold && event?.pricePerPersonAtThreshold != null ? event.pricePerPersonAtThreshold : event?.pricePerPerson;
  // Once the threshold is hit, the car fee drops entirely — flat per-person rate.
  const carTierPricePreview = collectsCarPerson
    ? overThreshold && event?.pricePerPersonAtThreshold != null
      ? (currentPerPersonRate || 0) * partySize
      : (currentPerPersonRate || 0) * partySize + (event?.pricePerCar || 0) * carCount
    : null;

  const loadEvent = async (overrideCode) => {
    setLoading(true);
    setIsPrivateLocked(false);
    try {
      const codeToUse = overrideCode ?? codeFromUrl ?? manualCode;
      const qs = new URLSearchParams();
      if (codeToUse) qs.set('code', codeToUse);
      if (referralCode) qs.set('ref', referralCode);
      if (customer?.id) qs.set('customerId', customer.id);
      const res = await fetch(`${API_BASE}/events/public/${eventSlug}${qs.toString() ? `?${qs.toString()}` : ''}`, {
        headers: getAuthHeaders(),
      });
      const json = await res.json();
      if (res.status === 403 && json.error === 'private_event') {
        setIsPrivateLocked(true);
        return;
      }
      if (!res.ok) throw new Error(json.error || 'Event not found');
      setEvent(json.event);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadEvent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventSlug]);

  // If the initial anonymous load hit the invite-only lock, retry once customer auth resolves —
  // an attendee who already has a ticket shouldn't need the code again just because auth loaded
  // after the first fetch (e.g. arriving here from a 'My Events' link with no code in the URL).
  useEffect(() => {
    if (isPrivateLocked && customer?.id) {
      loadEvent();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPrivateLocked, customer?.id]);

  const handleUnlockWithCode = (e) => {
    e.preventDefault();
    if (!manualCode.trim()) return;
    loadEvent(manualCode.trim());
  };

  useEffect(() => {
    fetchStripePublishableKey().then((key) => {
      if (key) setStripePromise(loadStripe(key));
    });
  }, []);

  const startCardSetup = async () => {
    if (!event || !customer) return;
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/events/${event.id}/setup-intent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ customerId: customer.id }),
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.error || 'Could not start signup');
      setClientSecret(json.data.clientSecret);
      setStripeCustomerId(json.data.stripeCustomerId);
    } catch (err) {
      setError(err.message);
    }
  };

  const completeSignup = async (stripePaymentMethodId) => {
    if (event.collectEmergencyContact && (!emergencyContactName.trim() || !emergencyContactPhone.trim())) {
      setError('Emergency contact name and phone are required for this event');
      setSavingCard(false);
      return;
    }
    try {
      const res = await fetch(`${API_BASE}/events/${event.id}/tickets`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({
          customerId: customer.id,
          stripeCustomerId,
          stripePaymentMethodId,
          referralCode: referralCode || undefined,
          ...(collectsCarPerson ? { carCount, partySize } : {}),
          ...(event.collectEmergencyContact ? { emergencyContactName: emergencyContactName.trim(), emergencyContactPhone: emergencyContactPhone.trim() } : {}),
          ...(dietaryPreference ? { dietaryPreference } : {}),
        }),
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.error || 'Signup failed');
      setSignupResult(json);
      loadEvent();
      loadMyReferralLink();
      loadAddOns();
    } catch (err) {
      setError(err.message);
      setSavingCard(false);
    }
  };

  const loadMyReferralLink = async () => {
    if (!customer) return;
    try {
      const res = await fetch(`${API_BASE}/referrals/my-code?customerId=${customer.id}`, {
        headers: getAuthHeaders(),
      });
      const json = await res.json();
      if (json.shareUrl) {
        const eventUrl = `${window.location.origin}/events/${eventSlug}?ref=${json.code}`;
        setMyReferralLink(eventUrl);
      }
    } catch (err) {
      console.warn('Could not load referral code:', err.message);
    }
  };

  const loadAddOns = async () => {
    if (!event) return;
    try {
      const qs = customer ? `?customerId=${customer.id}` : '';
      const res = await fetch(`${API_BASE}/events/${event.id}/addons${qs}`, { headers: getAuthHeaders() });
      const json = await res.json();
      if (json.success) setAddOns(json.data || []);
    } catch (err) {
      console.warn('Could not load add-ons:', err.message);
    }
  };

  const loadVenueTiers = async () => {
    if (!event) return;
    try {
      const res = await fetch(`${API_BASE}/events/${event.id}/venue-tiers`, { headers: getAuthHeaders() });
      const json = await res.json();
      if (json.success) setVenueTiers(json.data || []);
    } catch (err) {
      console.warn('Could not load venue tiers:', err.message);
    }
  };

  useEffect(() => {
    if (event?.id && isAuthenticated && isCarTiers) loadVenueTiers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event?.id, isAuthenticated]);

  const loadMyTicket = async () => {
    if (!event || !customer) return;
    try {
      const res = await fetch(`${API_BASE}/events/${event.id}/tickets/my?customerId=${customer.id}`, { headers: getAuthHeaders() });
      const json = await res.json();
      if (json.success && json.data) setSignupResult({ data: json.data, waitlisted: false });
    } catch (err) {
      console.warn('Could not load existing ticket:', err.message);
    }
  };

  useEffect(() => {
    if (event?.id && isAuthenticated && !signupResult) loadMyTicket();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event?.id, isAuthenticated]);

  const startPaymentRetry = async () => {
    if (!event || !customer) return;
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/events/${event.id}/setup-intent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ customerId: customer.id }),
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.error || 'Could not start retry');
      setRetryClientSecret(json.data.clientSecret);
      setRetryStripeCustomerId(json.data.stripeCustomerId);
    } catch (err) {
      setError(err.message);
    }
  };

  const completeRetry = async (stripePaymentMethodId) => {
    try {
      const res = await fetch(`${API_BASE}/events/${event.id}/tickets/retry-payment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ customerId: customer.id, stripeCustomerId: retryStripeCustomerId, stripePaymentMethodId }),
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.error || 'Payment failed');
      setSignupResult({ data: json.data, waitlisted: false });
      setRetryClientSecret(null);
      setRetryStripeCustomerId(null);
    } catch (err) {
      setError(err.message);
      setRetrySaving(false);
    }
  };

  const handleCancelSignup = async () => {
    if (!event || !customer) return;
    if (!window.confirm("Cancel your spot? You'll need to sign up again if you change your mind.")) return;
    setCancelling(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/events/${event.id}/tickets/my?customerId=${customer.id}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.error || 'Could not cancel');
      setSignupResult(null);
      setMyParty(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setCancelling(false);
    }
  };

  const handleProposeAddOn = async () => {
    if (!event || !customer) return;
    const { name, unitCost, unitYield, minSharePerPerson, unitYieldLabel } = proposedAddOnForm;
    if (!name || !unitCost || !minSharePerPerson) {
      setError('Name, unit cost, and minimum share per person are required');
      return;
    }
    setProposingAddOn(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/events/${event.id}/addons`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({
          customerId: customer.id,
          name,
          unitCost: Number(unitCost),
          unitYield: unitYield ? Number(unitYield) : undefined,
          unitYieldLabel: unitYieldLabel || undefined,
          minSharePerPerson: Number(minSharePerPerson),
        }),
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.error || 'Could not propose add-on');
      setProposedAddOnForm({ name: '', unitCost: '', unitYield: '', minSharePerPerson: '', unitYieldLabel: '' });
      loadAddOns();
    } catch (err) {
      setError(err.message);
    } finally {
      setProposingAddOn(false);
    }
  };

  const loadContributionsPublic = async () => {
    if (!event) return;
    try {
      const res = await fetch(`${API_BASE}/events/${event.id}/contributions/public`, { headers: getAuthHeaders() });
      const json = await res.json();
      if (json.success) setContributionsPublic({ data: json.data || [], runningTotal: json.runningTotal || 0, budgetCap: json.budgetCap });
    } catch (err) {
      console.warn('Could not load contributions:', err.message);
    }
  };

  const loadChatMessages = async (topicId) => {
    if (!event) return;
    try {
      const qs = topicId ? `?topicId=${topicId}` : '';
      const res = await fetch(`${CHAT_BASE}/${event.id}/messages${qs}`, { headers: getAuthHeaders() });
      const json = await res.json();
      if (json.success) {
        setChatMessages(json.data || []);
        setChatLoadError(null);
      } else {
        setChatLoadError(json.error || 'Could not load chat');
      }
    } catch (err) {
      setChatLoadError(err.message || 'Could not load chat');
    }
  };

  const loadChatTopics = async () => {
    if (!event) return;
    try {
      const res = await fetch(`${CHAT_BASE}/${event.id}/topics`, { headers: getAuthHeaders() });
      const json = await res.json();
      if (json.success) setChatTopics(json.data || []);
    } catch (err) {
      console.warn('Could not load chat topics:', err.message);
    }
  };

  const loadRoles = async () => {
    if (!event) return;
    try {
      const res = await fetch(`${API_BASE}/events/${event.id}/roles`, { headers: getAuthHeaders() });
      const json = await res.json();
      if (json.success) setRoles(json.data || []);
    } catch (err) {
      console.warn('Could not load roles:', err.message);
    }
  };

  const loadInventory = async () => {
    if (!event) return;
    try {
      const res = await fetch(`${API_BASE}/events/${event.id}/inventory`, { headers: getAuthHeaders() });
      const json = await res.json();
      if (json.success) setInventoryItems(json.data || []);
    } catch (err) {
      console.warn('Could not load inventory:', err.message);
    }
  };

  const loadMyParty = async () => {
    if (!event || !customer) return;
    try {
      const res = await fetch(`${API_BASE}/events/${event.id}/parties/mine?customerId=${customer.id}`, { headers: getAuthHeaders() });
      const json = await res.json();
      if (json.success) setMyParty(json.data || null);
    } catch (err) {
      console.warn('Could not load party:', err.message);
    }
  };

  const handleFormParty = async () => {
    if (!event || !customer) return;
    setPartyBusy(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/events/${event.id}/parties`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ customerId: customer.id, name: partyNameInput.trim() || undefined }),
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.error || 'Could not form party');
      setMyParty(json.data);
      setPartyNameInput('');
    } catch (err) {
      setError(err.message);
    } finally {
      setPartyBusy(false);
    }
  };

  const handleJoinParty = async () => {
    if (!event || !customer || !joinPartyId.trim()) return;
    setPartyBusy(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/events/${event.id}/parties/${joinPartyId.trim()}/join`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ customerId: customer.id }),
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.error || 'Could not join party');
      setMyParty(json.data);
      setJoinPartyId('');
    } catch (err) {
      setError(err.message);
    } finally {
      setPartyBusy(false);
    }
  };

  const handleLeaveParty = async () => {
    if (!event || !customer || !myParty) return;
    setPartyBusy(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/events/${event.id}/parties/${myParty.id}/leave?customerId=${customer.id}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.error || 'Could not leave party');
      setMyParty(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setPartyBusy(false);
    }
  };

  useEffect(() => {
    if (signupResult) {
      loadMyReferralLink();
      loadAddOns();
      loadContributionsPublic();
      loadRoles();
      loadInventory();
      loadChatTopics();
      loadChatMessages(selectedTopicId);
      loadMyParty();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signupResult?.data?.id]);

  useEffect(() => {
    if (signupResult) loadChatMessages(selectedTopicId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTopicId]);

  const handleSendChatMessage = async () => {
    if (!chatInput.trim() && !chatPhotoFile) return;
    setSendingChat(true);
    setError(null);
    try {
      let photoUrl = null;
      if (chatPhotoFile) {
        const uploaded = await uploadFile(chatPhotoFile, { fileType: 'ATTACHMENT' });
        photoUrl = uploaded.url;
      }
      const res = await fetch(`${CHAT_BASE}/${event.id}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({
          content: chatInput.trim() || `Offering: ${chatItemName}`,
          topicId: selectedTopicId || undefined,
          photoUrl,
          inventoryItemName: showItemOffer ? chatItemName || undefined : undefined,
        }),
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.error || 'Could not send message');
      setChatInput('');
      setChatPhotoFile(null);
      setChatItemName('');
      setShowItemOffer(false);
      loadChatMessages(selectedTopicId);
      if (json.data?.linkedInventoryItemId) loadInventory();
    } catch (err) {
      setError(err.message);
    } finally {
      setSendingChat(false);
    }
  };

  const handleClaimLeader = async (roleId) => {
    try {
      const res = await fetch(`${API_BASE}/events/${event.id}/roles/${roleId}/claim-leader`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ customerId: customer.id }),
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.error || 'Could not claim role');
      loadRoles();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleJoinRole = async (roleId) => {
    try {
      const res = await fetch(`${API_BASE}/events/${event.id}/roles/${roleId}/join`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ customerId: customer.id }),
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.error || 'Could not join role');
      loadRoles();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleLeaveRole = async (roleId) => {
    try {
      const res = await fetch(`${API_BASE}/events/${event.id}/roles/${roleId}/leave?customerId=${customer.id}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.error || 'Could not leave role');
      loadRoles();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleAddNeed = async () => {
    if (!newNeedName.trim()) return;
    setAddingNeed(true);
    try {
      const res = await fetch(`${API_BASE}/events/${event.id}/inventory`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ name: newNeedName.trim(), category: newNeedCategory }),
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.error || 'Could not add need');
      setNewNeedName('');
      loadInventory();
    } catch (err) {
      setError(err.message);
    } finally {
      setAddingNeed(false);
    }
  };

  const handleOfferOnNeed = async (itemId) => {
    if (!customer) return;
    setOfferingBusy(true);
    setError(null);
    try {
      let photoUrl = null;
      if (offerPhotoFile) {
        const uploaded = await uploadFile(offerPhotoFile, { fileType: 'ATTACHMENT' });
        photoUrl = uploaded.url;
      }
      const res = await fetch(`${API_BASE}/events/${event.id}/inventory/${itemId}/offer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ customerId: customer.id, photoUrl }),
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.error || 'Could not offer item');
      setOfferingItemId(null);
      setOfferPhotoFile(null);
      loadInventory();
    } catch (err) {
      setError(err.message);
    } finally {
      setOfferingBusy(false);
    }
  };

  const handleWithdrawOffer = async (itemId) => {
    if (!customer) return;
    try {
      const res = await fetch(`${API_BASE}/events/${event.id}/inventory/${itemId}/offer?customerId=${customer.id}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.error || 'Could not withdraw offer');
      loadInventory();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleAddOnSignup = async (addOnId, quantity = 1) => {
    try {
      const res = await fetch(`${API_BASE}/events/${event.id}/addons/${addOnId}/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ customerId: customer.id, quantity }),
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.error || 'Could not join add-on');
      loadAddOns();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleContributionSubmit = async (e) => {
    e.preventDefault();
    if (!contributionForm.description) return;
    try {
      let photoUrl = null;
      if (contributionPhotoFile) {
        const uploaded = await uploadFile(contributionPhotoFile, { fileType: 'ATTACHMENT' });
        photoUrl = uploaded.url;
      }
      const res = await fetch(`${API_BASE}/events/${event.id}/contributions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({
          customerId: customer.id,
          description: contributionForm.description,
          estimatedCost: contributionForm.estimatedCost ? Number(contributionForm.estimatedCost) : undefined,
          photoUrl,
        }),
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.error || 'Could not submit contribution');
      setContributionSubmitted(true);
      setContributionForm({ description: '', estimatedCost: '' });
      setContributionPhotoFile(null);
      loadContributionsPublic();
    } catch (err) {
      setError(err.message);
    }
  };

  const elementsOptions = useMemo(() => (clientSecret ? { clientSecret } : null), [clientSecret]);

  if (loading) return <div style={{ padding: 40, textAlign: 'center' }}>Loading...</div>;
  if (isPrivateLocked) {
    return (
      <div style={{ maxWidth: 420, margin: '0 auto', padding: '60px 20px', textAlign: 'center' }}>
        <h2>This is an invite-only event</h2>
        <p style={{ color: '#666' }}>Ask the organizer for your invite link, or enter your code below.</p>
        <form onSubmit={handleUnlockWithCode} style={{ display: 'flex', gap: 8, marginTop: 16 }}>
          <input
            placeholder="Invite code"
            value={manualCode}
            onChange={(e) => setManualCode(e.target.value)}
            style={{ flex: 1, padding: 10, borderRadius: 6, border: '1px solid #ccc' }}
          />
          <button type="submit" style={{ padding: '10px 16px', cursor: 'pointer' }}>Unlock</button>
        </form>
      </div>
    );
  }
  if (error && !event) return <div style={{ padding: 40, textAlign: 'center', color: 'crimson' }}>{error}</div>;
  if (!event) return null;

  const progress = event.progress;

  return (
    <div style={{ maxWidth: 640, margin: '0 auto', padding: '40px 20px' }}>
      {event.coverImage && (
        <img src={event.coverImage} alt={event.name} style={{ width: '100%', borderRadius: 12, marginBottom: 24 }} />
      )}
      <h1>{event.name}</h1>
      {event.date && <p>{new Date(event.date).toLocaleString()}</p>}
      {event.location && <p>{event.location}</p>}
      {event.description && <p style={{ whiteSpace: 'pre-wrap' }}>{event.description}</p>}

      {event.videoUrl && (
        <video src={event.videoUrl} controls style={{ width: '100%', borderRadius: 12, marginBottom: 24 }} />
      )}

      {event.scheduleText && (
        <div style={{ marginBottom: 20 }}>
          <h3 style={{ marginBottom: 6 }}>Schedule</h3>
          <p style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{event.scheduleText}</p>
        </div>
      )}

      {event.safetyNote && (
        <div style={{ marginBottom: 20, padding: 14, background: '#fff8e1', borderRadius: 8, border: '1px solid #ffe082' }}>
          <h3 style={{ marginTop: 0, marginBottom: 6 }}>Safety</h3>
          <p style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{event.safetyNote}</p>
        </div>
      )}

      {event.digitalFileUrl && (
        <p style={{ marginBottom: 20 }}>
          <a href={event.digitalFileUrl} target="_blank" rel="noreferrer">📍 Site map / arrival instructions</a>
        </p>
      )}

      {progress && (
        <div style={{ margin: '20px 0', padding: 16, background: '#f6fbff', borderRadius: 8, border: '1px solid #dbeafe' }}>
          <strong>{progress.confirmedCount} confirmed</strong>
          {progress.chargeThresholdAttendees ? ` of ${progress.chargeThresholdAttendees} needed` : ''}
          {event.maxCapacity ? ` (max ${event.maxCapacity})` : ''}
          {progress.waitlistCount > 0 && <span> · {progress.waitlistCount} on the waitlist — join in and be part of the momentum!</span>}
          {event.maxCapacity && (
            <p style={{ margin: '8px 0 0', fontSize: 13, color: '#666' }}>
              Once we hit {event.maxCapacity} people we'll close signups — anyone after that goes on the list for our next one.
            </p>
          )}
          {progress.minAttendeesToConfirm != null && progress.confirmedCount < progress.minAttendeesToConfirm && (
            <p style={{ margin: '8px 0 0', fontSize: 14 }}>
              We need {progress.minAttendeesToConfirm - progress.confirmedCount} more to lock the site in.
            </p>
          )}
          {hasCarPersonPricing && (
            <p style={{ margin: '8px 0 0', fontSize: 14 }}>
              {overThreshold && event.pricePerPersonAtThreshold != null ? (
                <>
                  Current rate: ${event.pricePerPersonAtThreshold}/person (no car fee) — locked in, we hit {event.chargeThresholdAttendees}.
                  {event.chargeBatchTriggeredAt
                    ? ' Cards have been charged.'
                    : event.thresholdReachedAt
                      ? ` Cards will be charged ${new Date(new Date(event.thresholdReachedAt).getTime() + 3 * 24 * 60 * 60 * 1000).toLocaleDateString()}.`
                      : ''}
                </>
              ) : (
                <>
                  Current rate: ${event.pricePerCar ?? 0}/car + ${currentPerPersonRate ?? 0}/person
                  {event.chargeThresholdAttendees && event.pricePerPersonAtThreshold != null && (
                    <> — drops the car fee and jumps to a flat ${event.pricePerPersonAtThreshold}/person for everyone if we reach {event.chargeThresholdAttendees} (you'll get notified, then charged 3 days later)</>
                  )}
                </>
              )}
              {event.registrationDeadline && (
                <> · nobody's charged until {new Date(event.registrationDeadline).toLocaleDateString()} or we hit {event.chargeThresholdAttendees}, whichever comes first</>
              )}
            </p>
          )}
        </div>
      )}

      {error && <div style={{ color: 'crimson', margin: '12px 0' }}>{error}</div>}

      {isCarTiers && (
        <div style={{ margin: '20px 0', padding: 16, background: '#f6fbff', borderRadius: 8, border: '1px solid #dbeafe' }}>
          <strong>Venue: we'll book the site that fits everyone</strong>
          {event.venueSelectionDate && (
            <p style={{ margin: '6px 0', fontSize: 14 }}>
              Decided on {new Date(event.venueSelectionDate).toLocaleDateString()} based on confirmed cars.
            </p>
          )}
          {venueTiers.length > 0 && (
            <ul style={{ margin: '8px 0 0', paddingLeft: 20, fontSize: 14 }}>
              {venueTiers.map((tier) => (
                <li key={tier.id} style={{ fontWeight: event.selectedVenueTierId === tier.id ? 700 : 400 }}>
                  {tier.name} — up to {tier.maxCars} cars{tier.location ? ` (${tier.location})` : ''}
                  {event.selectedVenueTierId === tier.id ? ' ✅ selected' : ''}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {!signupResult && (
        <>
          {!isAuthenticated ? (
            <p>
              <Link to="/customer/login">Log in</Link> or <Link to="/customer/signup">create an account</Link> to sign up for this event.
            </p>
          ) : !isDeferred && !isCarTiers ? (
            <p>Ticketing for this event isn't set up for online signup yet — please contact us.</p>
          ) : !clientSecret ? (
            <>
              {collectsCarPerson && (
                <div style={{ marginBottom: 16, display: 'flex', gap: 12, alignItems: 'flex-end' }}>
                  <label>
                    Cars<br />
                    <input type="number" min={0} value={carCount} onChange={(e) => setCarCount(Math.max(0, Number(e.target.value) || 0))} style={{ width: 80, padding: 8, borderRadius: 6, border: '1px solid #ccc' }} />
                    <span style={{ display: 'block', fontSize: 11, color: '#888' }}>0 if riding with someone</span>
                  </label>
                  <label>
                    People<br />
                    <input type="number" min={1} value={partySize} onChange={(e) => setPartySize(Math.max(1, Number(e.target.value) || 1))} style={{ width: 80, padding: 8, borderRadius: 6, border: '1px solid #ccc' }} />
                  </label>
                  <div style={{ fontSize: 16, fontWeight: 600 }}>${carTierPricePreview?.toFixed(2)}</div>
                </div>
              )}
              {event.collectEmergencyContact && (
                <div style={{ marginBottom: 16, display: 'flex', gap: 12 }}>
                  <input
                    placeholder="Emergency contact name"
                    value={emergencyContactName}
                    onChange={(e) => setEmergencyContactName(e.target.value)}
                    style={{ flex: 1, padding: 10, borderRadius: 6, border: '1px solid #ccc' }}
                  />
                  <input
                    placeholder="Emergency contact phone"
                    value={emergencyContactPhone}
                    onChange={(e) => setEmergencyContactPhone(e.target.value)}
                    style={{ flex: 1, padding: 10, borderRadius: 6, border: '1px solid #ccc' }}
                  />
                </div>
              )}
              <div style={{ marginBottom: 16 }}>
                <label style={{ fontSize: 13, display: 'block', marginBottom: 6 }}>Coming to the Saturday asado? Meat or veg?</label>
                <div style={{ display: 'flex', gap: 12 }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 14 }}>
                    <input type="radio" name="dietary" checked={dietaryPreference === 'meat'} onChange={() => setDietaryPreference('meat')} /> Meat
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 14 }}>
                    <input type="radio" name="dietary" checked={dietaryPreference === 'veg'} onChange={() => setDietaryPreference('veg')} /> Veg
                  </label>
                </div>
              </div>
              <button onClick={startCardSetup} style={{ padding: '12px 24px', fontSize: 16, cursor: 'pointer' }}>
                {event.maxCapacity && progress && progress.confirmedCount >= event.maxCapacity ? 'Join the waitlist' : "Sign up — you won't be charged yet"}
              </button>
            </>
          ) : stripePromise && elementsOptions ? (
            <Elements stripe={stripePromise} options={elementsOptions}>
              <CardSetupForm onSaved={completeSignup} saving={savingCard} setSaving={setSavingCard} setError={setError} />
            </Elements>
          ) : (
            <p>Loading payment form...</p>
          )}
        </>
      )}

      {signupResult && (
        <div style={{ marginTop: 24 }}>
          {signupResult.waitlisted ? (
            <div style={{ padding: 16, background: '#fff3cd', borderRadius: 8, border: '1px solid #ffc107' }}>
              <strong>You're on the waitlist!</strong>
              <p>We'll charge your card and confirm your spot if the event expands.</p>
            </div>
          ) : signupResult.data?.status === 'payment_failed' ? (
            <div style={{ padding: 16, background: '#fdecea', borderRadius: 8, border: '1px solid #dc3545' }}>
              <strong>Your payment didn't go through</strong>
              <p>The guest list is locked in, but we couldn't charge the card on file for your spot. Update your card to secure it.</p>
              {!retryClientSecret ? (
                <button onClick={startPaymentRetry} style={{ marginTop: 8, padding: '10px 20px', cursor: 'pointer' }}>
                  Update card
                </button>
              ) : stripePromise ? (
                <Elements stripe={stripePromise} options={{ clientSecret: retryClientSecret }}>
                  <CardSetupForm onSaved={completeRetry} saving={retrySaving} setSaving={setRetrySaving} setError={setError} />
                </Elements>
              ) : (
                <p>Loading payment form...</p>
              )}
            </div>
          ) : signupResult.data?.status === 'valid' ? (
            <div style={{ padding: 16, background: '#e8f5e9', borderRadius: 8, border: '1px solid #28a745' }}>
              <strong>You're confirmed and charged — see you there!</strong>
            </div>
          ) : (
            <div style={{ padding: 16, background: '#e8f5e9', borderRadius: 8, border: '1px solid #28a745' }}>
              <strong>You're confirmed to attend!</strong>
              <p>Your card is saved but you won't be charged until we hit the signup threshold.</p>
              <button onClick={handleCancelSignup} disabled={cancelling} style={{ padding: '8px 14px', fontSize: 13, cursor: 'pointer', background: 'transparent', border: '1px solid #dc3545', color: '#dc3545', borderRadius: 6 }}>
                {cancelling ? 'Cancelling...' : 'Cancel my spot'}
              </button>
            </div>
          )}

          {myReferralLink && (
            <div style={{ marginTop: 20 }}>
              <h3>Invite friends</h3>
              <p>Share your personal link — we track who invited who so we can keep this a positive space.</p>
              <input
                readOnly
                value={myReferralLink}
                onClick={(e) => e.target.select()}
                style={{ width: '100%', padding: 10, borderRadius: 6, border: '1px solid #ccc' }}
              />
            </div>
          )}

          <div style={{ marginTop: 24 }}>
            <h3>Camping party</h3>
            <p style={{ fontSize: 13, color: '#888', marginTop: -6, marginBottom: 10 }}>
              Form a party with whoever you're camping with — same tent, same car, or just want to be nearby.
              We'll assign parties to sites once the guest list is locked in, and send out who's where before the weekend.
            </p>
            {myParty ? (
              <div style={{ padding: 14, background: '#fff', borderRadius: 8, border: '1px solid #e0e0e0' }}>
                <strong>{myParty.name || `${myParty.leader?.firstName || 'Your'}'s crew`}</strong>
                <p style={{ margin: '6px 0', fontSize: 14, color: '#666' }}>
                  {myParty.members?.map((m) => `${m.ticket?.customer?.firstName || ''} ${m.ticket?.customer?.lastName || ''}`).join(', ')}
                </p>
                {myParty.assignedSite ? (
                  <p style={{ margin: '6px 0', fontSize: 14, fontWeight: 600 }}>
                    📍 Assigned to Site {myParty.assignedSite.siteNumber}{myParty.assignedSite.label ? ` (${myParty.assignedSite.label})` : ''}
                  </p>
                ) : (
                  <p style={{ margin: '6px 0', fontSize: 13, color: '#888' }}>Site assignment goes out once the guest list is locked in.</p>
                )}
                <div style={{ marginTop: 8 }}>
                  <label style={{ fontSize: 12, color: '#888' }}>Invite link (share with your crew):</label>
                  <input
                    readOnly
                    value={`${window.location.origin}/events/${eventSlug}${codeFromUrl ? `?code=${codeFromUrl}&` : '?'}party=${myParty.id}`}
                    onClick={(e) => e.target.select()}
                    style={{ width: '100%', padding: 8, marginTop: 4, borderRadius: 6, border: '1px solid #ccc', fontSize: 13 }}
                  />
                </div>
                <button onClick={handleLeaveParty} disabled={partyBusy} style={{ marginTop: 10, padding: '6px 12px', cursor: 'pointer' }}>
                  Leave party
                </button>
              </div>
            ) : (
              <div style={{ padding: 14, background: '#fff', borderRadius: 8, border: '1px solid #e0e0e0' }}>
                <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
                  <input
                    placeholder="Party name (optional, e.g. Sarah's Crew)"
                    value={partyNameInput}
                    onChange={(e) => setPartyNameInput(e.target.value)}
                    style={{ flex: 1, padding: 10, borderRadius: 6, border: '1px solid #ccc' }}
                  />
                  <button onClick={handleFormParty} disabled={partyBusy} style={{ padding: '10px 16px', cursor: 'pointer' }}>
                    Form a party
                  </button>
                </div>
                {partyIdFromUrl ? (
                  <button onClick={() => { setJoinPartyId(partyIdFromUrl); handleJoinParty(); }} disabled={partyBusy} style={{ padding: '10px 16px', cursor: 'pointer' }}>
                    Join the party you were invited to
                  </button>
                ) : (
                  <div style={{ display: 'flex', gap: 8 }}>
                    <input
                      placeholder="Paste a party invite link or ID to join one"
                      value={joinPartyId}
                      onChange={(e) => setJoinPartyId(e.target.value)}
                      style={{ flex: 1, padding: 10, borderRadius: 6, border: '1px solid #ccc' }}
                    />
                    <button onClick={handleJoinParty} disabled={partyBusy} style={{ padding: '10px 16px', cursor: 'pointer' }}>
                      Join
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          <div style={{ marginTop: 24 }}>
            <h3>Add-ons</h3>
            {addOns.map((addOn) => (
              <div key={addOn.id} style={{ padding: 14, marginBottom: 10, background: '#fff', borderRadius: 8, border: '1px solid #e0e0e0' }}>
                <strong>{addOn.name}</strong>
                {addOn.status === 'pending_approval' && <span style={{ marginLeft: 8, fontSize: 12, color: '#f57c00' }}>(pending approval)</span>}
                <p style={{ margin: '4px 0', fontSize: 14, color: '#666' }}>
                  {addOn.description} — currently ${addOn.pricing?.pricePerShare?.toFixed(2)} per share
                  {addOn.unitYieldLabel ? ` (${addOn.unitYieldLabel} per unit)` : ''}
                </p>
                {addOn.status === 'active' && (
                  <button onClick={() => handleAddOnSignup(addOn.id, 1)} style={{ padding: '8px 16px', cursor: 'pointer' }}>
                    Join in (1 share)
                  </button>
                )}
              </div>
            ))}

            <div style={{ padding: 14, background: '#f8f9fa', borderRadius: 8, border: '1px solid #e0e0e0', marginTop: 10 }}>
              <strong style={{ fontSize: 14 }}>Propose your own add-on</strong>
              <p style={{ margin: '4px 0 10px', fontSize: 13, color: '#666' }}>The organizer will need to approve it before others can join.</p>
              <input
                placeholder="Name (e.g. Coffee run)"
                value={proposedAddOnForm.name}
                onChange={(e) => setProposedAddOnForm((p) => ({ ...p, name: e.target.value }))}
                style={{ width: '100%', padding: 8, marginBottom: 8, borderRadius: 6, border: '1px solid #ccc' }}
              />
              <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                <input
                  placeholder="Unit cost ($)"
                  type="number"
                  value={proposedAddOnForm.unitCost}
                  onChange={(e) => setProposedAddOnForm((p) => ({ ...p, unitCost: e.target.value }))}
                  style={{ flex: 1, padding: 8, borderRadius: 6, border: '1px solid #ccc' }}
                />
                <input
                  placeholder="Unit yield (e.g. 10)"
                  type="number"
                  value={proposedAddOnForm.unitYield}
                  onChange={(e) => setProposedAddOnForm((p) => ({ ...p, unitYield: e.target.value }))}
                  style={{ flex: 1, padding: 8, borderRadius: 6, border: '1px solid #ccc' }}
                />
                <input
                  placeholder="Min share/person"
                  type="number"
                  value={proposedAddOnForm.minSharePerPerson}
                  onChange={(e) => setProposedAddOnForm((p) => ({ ...p, minSharePerPerson: e.target.value }))}
                  style={{ flex: 1, padding: 8, borderRadius: 6, border: '1px solid #ccc' }}
                />
              </div>
              <button onClick={handleProposeAddOn} disabled={proposingAddOn} style={{ padding: '8px 16px', cursor: 'pointer' }}>
                {proposingAddOn ? 'Submitting...' : 'Propose add-on'}
              </button>
            </div>
          </div>

          <div style={{ marginTop: 24 }}>
            <h3>Bring something?</h3>
            {contributionsPublic.data.length > 0 && (
              <div style={{ marginBottom: 12 }}>
                {contributionsPublic.data.map((c) => (
                  <div key={c.id} style={{ fontSize: 14, padding: '6px 0', borderBottom: '1px solid #eee', display: 'flex', alignItems: 'center', gap: 8 }}>
                    {c.photoUrl && <img src={c.photoUrl} alt="" style={{ width: 32, height: 32, objectFit: 'cover', borderRadius: 6 }} />}
                    <span>
                      <strong>{c.customerFirstName}</strong>: {c.description}
                      {c.estimatedCost ? ` (~$${c.estimatedCost})` : ''}
                    </span>
                  </div>
                ))}
                <p style={{ fontSize: 13, color: contributionsPublic.budgetCap != null && contributionsPublic.runningTotal > contributionsPublic.budgetCap ? 'crimson' : '#666', marginTop: 8 }}>
                  Running total: ${contributionsPublic.runningTotal.toFixed(2)}
                  {contributionsPublic.budgetCap != null ? ` of $${contributionsPublic.budgetCap.toFixed(2)} budget` : ''}
                </p>
              </div>
            )}
            {contributionSubmitted ? (
              <p>Thanks — we'll be in touch about reimbursement.</p>
            ) : (
              <form onSubmit={handleContributionSubmit}>
                <input
                  placeholder="What will you bring? (e.g. Tres leches cake)"
                  value={contributionForm.description}
                  onChange={(e) => setContributionForm((p) => ({ ...p, description: e.target.value }))}
                  style={{ width: '100%', padding: 10, marginBottom: 10, borderRadius: 6, border: '1px solid #ccc' }}
                />
                <input
                  type="number"
                  placeholder="Estimated ingredient cost ($)"
                  value={contributionForm.estimatedCost}
                  onChange={(e) => setContributionForm((p) => ({ ...p, estimatedCost: e.target.value }))}
                  style={{ width: '100%', padding: 10, marginBottom: 10, borderRadius: 6, border: '1px solid #ccc' }}
                />
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setContributionPhotoFile(e.target.files?.[0] || null)}
                  style={{ display: 'block', marginBottom: 10, fontSize: 13 }}
                />
                <button type="submit" style={{ padding: '10px 20px', cursor: 'pointer' }}>Offer to bring this</button>
              </form>
            )}
          </div>

          <div style={{ marginTop: 24 }}>
            <h3>Roles — pitch in</h3>
            {roles.length === 0 ? (
              <p style={{ fontSize: 14, color: '#888' }}>No roles set up yet.</p>
            ) : (
              roles.map((role) => (
                <div key={role.id} style={{ padding: 14, marginBottom: 10, background: '#fff', borderRadius: 8, border: '1px solid #e0e0e0' }}>
                  <strong>{role.name}</strong>
                  {role.description && <p style={{ margin: '4px 0', fontSize: 14, color: '#666' }}>{role.description}</p>}
                  <p style={{ fontSize: 14, margin: '4px 0' }}>
                    Leader: {role.leader ? `${role.leader.firstName} ${role.leader.lastName}` : <em>unclaimed</em>}
                  </p>
                  {role.helpers?.length > 0 && (
                    <p style={{ fontSize: 14, margin: '4px 0' }}>
                      Helpers: {role.helpers.map((h) => `${h.customer.firstName} ${h.customer.lastName}`).join(', ')}
                    </p>
                  )}
                  <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                    {!role.leader && (
                      <button onClick={() => handleClaimLeader(role.id)} style={{ padding: '6px 12px', cursor: 'pointer' }}>Become leader</button>
                    )}
                    <button onClick={() => handleJoinRole(role.id)} style={{ padding: '6px 12px', cursor: 'pointer' }}>Join as helper</button>
                    <button onClick={() => handleLeaveRole(role.id)} style={{ padding: '6px 12px', cursor: 'pointer' }}>Leave</button>
                  </div>
                </div>
              ))
            )}
          </div>

          <div style={{ marginTop: 24 }}>
            <h3>Gear & supplies</h3>
            <p style={{ fontSize: 13, color: '#888', marginTop: -6, marginBottom: 10 }}>
              For the communal area (Sites 6 & 7). Multiple people can offer the same thing — they all show up under it.
            </p>
            {['gear', 'snacks', 'other'].map((cat) => {
              const items = inventoryItems.filter((it) => (it.category || 'gear') === cat || (cat === 'gear' && !it.category));
              if (items.length === 0) return null;
              return (
                <div key={cat} style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#888', textTransform: 'uppercase', marginBottom: 6 }}>
                    {cat === 'gear' ? 'Gear' : cat === 'snacks' ? 'Snacks' : 'Other'}
                  </div>
                  {items.map((item) => {
                    const myOffer = item.offers?.find((o) => o.customer?.id === customer?.id);
                    return (
                      <div key={item.id} style={{ padding: 10, marginBottom: 8, background: '#fff', borderRadius: 8, border: '1px solid #e0e0e0', fontSize: 14 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <strong>{item.name}</strong>
                          {!myOffer ? (
                            <button onClick={() => setOfferingItemId(item.id)} style={{ padding: '5px 12px', cursor: 'pointer', fontSize: 12 }}>
                              I have this
                            </button>
                          ) : (
                            <button onClick={() => handleWithdrawOffer(item.id)} style={{ padding: '5px 12px', cursor: 'pointer', fontSize: 12 }}>
                              Withdraw my offer
                            </button>
                          )}
                        </div>
                        {item.offers?.length > 0 && (
                          <div style={{ marginTop: 6, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                            {item.offers.map((o) => (
                              <div key={o.id} style={{ fontSize: 12, color: '#666', display: 'flex', alignItems: 'center', gap: 4 }}>
                                {o.photoUrl && <img src={o.photoUrl} alt="" style={{ width: 28, height: 28, objectFit: 'cover', borderRadius: 4 }} />}
                                {o.customer?.firstName} {o.status === 'confirmed' ? '✅' : o.status === 'declined' ? '❌' : ''}
                              </div>
                            ))}
                          </div>
                        )}
                        {offeringItemId === item.id && (
                          <div style={{ marginTop: 8, display: 'flex', gap: 8, alignItems: 'center' }}>
                            <input type="file" accept="image/*" onChange={(e) => setOfferPhotoFile(e.target.files?.[0] || null)} style={{ fontSize: 12 }} />
                            <button onClick={() => handleOfferOnNeed(item.id)} disabled={offeringBusy} style={{ padding: '5px 12px', cursor: 'pointer', fontSize: 12 }}>
                              {offeringBusy ? 'Saving...' : 'Confirm offer'}
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })}
            <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
              <input
                placeholder="Add something needed... (e.g. a big pot)"
                value={newNeedName}
                onChange={(e) => setNewNeedName(e.target.value)}
                style={{ flex: 1, padding: 10, borderRadius: 6, border: '1px solid #ccc' }}
              />
              <select value={newNeedCategory} onChange={(e) => setNewNeedCategory(e.target.value)} style={{ padding: 10, borderRadius: 6, border: '1px solid #ccc' }}>
                <option value="gear">Gear</option>
                <option value="snacks">Snacks</option>
                <option value="other">Other</option>
              </select>
              <button onClick={handleAddNeed} disabled={addingNeed} style={{ padding: '10px 16px', cursor: 'pointer' }}>
                {addingNeed ? 'Adding...' : 'Add'}
              </button>
            </div>
          </div>

          <div style={{ marginTop: 24 }}>
            <h3>Group chat</h3>
            <p style={{ fontSize: 13, color: '#888', marginTop: -6, marginBottom: 10 }}>
              One shared space — filter by topic below, or view everything under "All".
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
              <button
                onClick={() => setSelectedTopicId(null)}
                style={{
                  padding: '6px 14px', borderRadius: 20, cursor: 'pointer', border: '1px solid #ccc',
                  background: selectedTopicId === null ? '#333' : '#fff',
                  color: selectedTopicId === null ? '#fff' : '#333',
                }}
              >
                All
              </button>
              {chatTopics.map((topic) => (
                <button
                  key={topic.id}
                  onClick={() => setSelectedTopicId(topic.id)}
                  style={{
                    padding: '6px 14px', borderRadius: 20, cursor: 'pointer', border: '1px solid #ccc',
                    background: selectedTopicId === topic.id ? '#333' : '#fff',
                    color: selectedTopicId === topic.id ? '#fff' : '#333',
                  }}
                >
                  {topic.name}
                </button>
              ))}
            </div>
            <div style={{ maxHeight: 260, overflowY: 'auto', border: '1px solid #e0e0e0', borderRadius: 8, padding: 10, marginBottom: 10, background: '#fff' }}>
              {chatLoadError ? (
                <p style={{ fontSize: 14, color: 'crimson' }}>Couldn't load chat: {chatLoadError}</p>
              ) : chatMessages.length === 0 ? (
                <p style={{ fontSize: 14, color: '#888' }}>No messages yet — say hi!</p>
              ) : (
                chatMessages.map((msg) => (
                  <div key={msg.id} style={{ marginBottom: 10 }}>
                    <div style={{ fontSize: 13 }}>
                      <strong>{msg.isAdmin ? 'Organizer' : msg.isStaff ? 'Staff' : `${msg.customer?.firstName || ''} ${msg.customer?.lastName || ''}`}</strong>
                      {msg.topic?.name && <span style={{ color: '#888', marginLeft: 6 }}>#{msg.topic.name}</span>}
                      {msg.status === 'pending' && <span style={{ color: '#f57c00', marginLeft: 6 }}>(pending approval)</span>}
                    </div>
                    <p style={{ margin: '2px 0' }}>{msg.content}</p>
                    {msg.photoUrl && <img src={msg.photoUrl} alt="attachment" style={{ maxWidth: 160, borderRadius: 6 }} />}
                  </div>
                ))
              )}
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, marginBottom: 8, cursor: 'pointer' }}>
              <input type="checkbox" checked={showItemOffer} onChange={(e) => setShowItemOffer(e.target.checked)} />
              Offering gear/an item with this message
            </label>
            {showItemOffer && (
              <input
                placeholder="Item name (e.g. Generator, big pot)"
                value={chatItemName}
                onChange={(e) => setChatItemName(e.target.value)}
                style={{ width: '100%', padding: 10, marginBottom: 8, borderRadius: 6, border: '1px solid #ccc' }}
              />
            )}
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                placeholder={selectedTopicId ? `Message in #${chatTopics.find((t) => t.id === selectedTopicId)?.name || ''}...` : 'Message...'}
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                style={{ flex: 1, padding: 10, borderRadius: 6, border: '1px solid #ccc' }}
              />
              {showItemOffer && (
                <input type="file" accept="image/*" onChange={(e) => setChatPhotoFile(e.target.files?.[0] || null)} />
              )}
              <button onClick={handleSendChatMessage} disabled={sendingChat} style={{ padding: '10px 16px', cursor: 'pointer' }}>
                {sendingChat ? 'Sending...' : 'Send'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
